import React, { useState } from 'react';
import RackImage from './RackImage';
import { getRackHeights } from '../config/vendorRegistry';

// ============================================================================
// EXPORTED UTILITY to Calculate Rack Layout - can be used by other components if needed
// ============================================================================
export const calculateRackLayout = (config, productDb, rackSize=42) => {
  const layout = [];
  // MORK TODO : hardwareVendor can probably come from the vendor registry
  const hardwareVendor = config.hardwareVendor || 'AIC';
  const effectiveRackSize = rackSize === 0 ? 42 : rackSize;

  const dNodeCount = config.veloCount || 0;
  const fNodeCount = (config.afaEnabled ? config.afaNodeCount : 0) || 0;
  const sNodeCount = (config.hybridEnabled ? config.hybridNodeCount : 0) || 0;

  const dNodeSize = productDb.ssdPerVelo;
  const sfNodeSize = productDb.vpodsPerNode;

  // Rack units per item, from the same registry table the Rack Units metric
  // reads, so the drawing and the number agree (CONF-230). Supermicro Mixed
  // Fleet nodes are 2U. Every vendor's JBOD is 4U; Dell's old 8U layout for
  // non-standard bay counts is gone.
  const { director: dNodeH, afa: fNodeH, hybrid: sNodeH, jbod: jbodHeight } = getRackHeights(hardwareVendor);

  let totalHeight = (dNodeCount * dNodeH) + (fNodeCount * fNodeH) + (sNodeCount * (sNodeH + jbodHeight));

  let spacingCount = 0;
  if (dNodeCount > 0 && (fNodeCount > 0 || sNodeCount > 0)) spacingCount++;
  if (fNodeCount > 0 && sNodeCount > 0) spacingCount++;

  const racksWithoutSpacing = Math.ceil(totalHeight / effectiveRackSize);
  const totalHeightWithSpacing = totalHeight + spacingCount;
  const racksWithSpacing = Math.ceil(totalHeightWithSpacing / effectiveRackSize);
  const useSpacing = racksWithSpacing <= racksWithoutSpacing;
  totalHeight = useSpacing ? totalHeightWithSpacing : totalHeight;

  const emptyRack = () => Array(effectiveRackSize).fill({ type: 'empty' });
  const numRacks = Math.min(10, Math.ceil(totalHeight / effectiveRackSize));
  for (let i = 0; i < numRacks; i++) {
    layout.push(emptyRack());
  }
  // numRacks is arithmetic (total U / rack U), but a unit cannot straddle two
  // racks, so the packing below can need one more than that — and always does
  // past the cap of ten. The loops used to write into a rack that did not exist
  // and throw (calculateRackLayout.test.js). A rack is added the moment the
  // packer steps into it, so every unit lands somewhere.
  const rackAt = (index) => {
    while (layout.length <= index) layout.push(emptyRack());
    return layout[index];
  };

  let rackIndex = 0;
  let position = 0;

  for (let i = 0; i < dNodeCount; i++) {
    if (position + dNodeH > effectiveRackSize) { rackIndex++; position = 0; }
    rackAt(rackIndex)[position] = { type: 'dNode', heightU: dNodeH, label: `D-Node ${i + 1}`, ssdInfo: `${dNodeSize} × ${config.veloSsdCapacity}TB` };
    position += dNodeH;
  }
  if (useSpacing && dNodeCount > 0 && (fNodeCount > 0 || sNodeCount > 0)) {
    if (position >= effectiveRackSize) { rackIndex++; position = 0; } else { position++; }
  }

  for (let i = 0; i < fNodeCount; i++) {
    if (position + fNodeH > effectiveRackSize) { rackIndex++; position = 0; }
    rackAt(rackIndex)[position] = { type: 'fNode', heightU: fNodeH, label: `F-Node ${i + 1}`, ssdInfo: `${sfNodeSize} × ${config.afaVpodSsdCapacity}TB` };
    position += fNodeH;
  }
  if (useSpacing && fNodeCount > 0 && sNodeCount > 0) {
    if (position >= effectiveRackSize) { rackIndex++; position = 0; } else { position++; }
  }

  for (let i = 0; i < sNodeCount; i++) {
    if (position + jbodHeight + sNodeH > effectiveRackSize) { rackIndex++; position = 0; }
    rackAt(rackIndex)[position] = { type: 'sNode', heightU: sNodeH, label: `S-Node ${i + 1}`, ssdInfo: `${sfNodeSize} × ${config.hybridVpodSsdCapacity}TB` };
    position += sNodeH;
    rackAt(rackIndex)[position] = { type: 'jbod', heightU: jbodHeight, label: `JBOD ${i + 1}`, jbodLabel: `${config.jbodSize}-slot ${config.hybridHddCapacity}TB`, isJbodOnly: true };
    position += jbodHeight;
  }

  return layout;
};

const RealisticRackVisualization = ({ config, metrics, productDb, onRackSizeChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rackSize, setRackSize] = useState(42); // Default rack size is 42U
  const [showRack, setShowRack] = useState(true); // New variable to track if rack frame should be shown
  const [selectedDropdownValue, setSelectedDropdownValue] = useState(42); // Track what's selected in dropdown


  // Get the current hardware vendor from config
  const hardwareVendor = config.hardwareVendor || 'AIC';
  
  // Define colors for different node types (can be vendor-specific if desired)
  const colors = {
    dNode: '#007bff', // Blue
    fNode: '#28a745', // Green
    sNode: '#fd7e14', // Orange
    jbod: '#6c757d',  // Gray
    'jbod-8u': '#6c757d', // Same gray for 8U JBOD
    empty: '#343a40'   // Dark gray
  };

  let rackLayout;
  try {
      rackLayout = calculateRackLayout(config, productDb, rackSize);
  } catch (error) {
    console.error("Failed to calculate rack layout:", error);
    rackLayout = [Array(rackSize).fill({ type: 'empty' })];
  }
  
  // Function to render a single rack with actual SVG components
  const renderRack = (rack, rackIndex, isDetailView = false) => {
    // Maintain exact 21" width (converted to pixels)
    const rackWidthInches = 21;
    
    // Use the standard 1.75" per 1U height ratio
    const rackUnitRatio = 1.75; // 1.75 inches per 1U
    
    // Calculate the pixel values based on a consistent scale
    const pixelsPerInch = isDetailView ? 10 : 7; // Scale factor
    
    const rackWidth = rackWidthInches * pixelsPerInch;
    const unitHeight = rackUnitRatio * pixelsPerInch;
    const rackHeight = rackSize * rackUnitRatio * pixelsPerInch;
    
    // Component width should be slightly less than rack width to account for rails
    const componentWidth = 19 * pixelsPerInch;
    
    // Add label area on the left side - wider in detail view to accommodate SSD info
    const labelAreaWidth = isDetailView ? 180 : 0; // No label area in normal view
    const totalWidth = rackWidth + labelAreaWidth;
    
    return (
      <div 
        key={`rack-${rackIndex}`} 
        className="rack-container mb-4 relative"
        style={{ 
          width: `${totalWidth}px`,
        }}
      >
        <div className="rack-label text-sm mb-1 text-center">Rack {rackIndex + 1}</div>
        
        <div className="flex">
          {/* Labels area - only for detailed view and non-empty components */}
          {isDetailView && (
            <div 
              className="flex flex-col" 
              style={{ 
                width: `${labelAreaWidth}px`,
                height: `${rackHeight}px`,
                position: 'relative'
              }}
            >
              {rack.map((unit, index) => {
                if (unit.type === 'empty') return null;
                
                // Determine component properties for label
                let componentHeightInU;
                let labelTitle = '';
                let labelDetails = '';

                if (unit.isJbodOnly) {
                  // Handle JBOD labels
                  const isJbod8U = unit.type === 'jbod-8u';
                  componentHeightInU = unit.heightU || (isJbod8U ? 8 : 4); // Standard JBOD is 4U, Dell Hybrid is 8U
                  const jbodRU = componentHeightInU;
                  labelTitle = unit.label; // e.g., "JBOD 1"
                  labelDetails = `(${jbodRU}U, ${config.jbodSize}-slot, ${config.hybridHddCapacity}TB HDD)`;
                } else {
                  // Handle Node labels (D, F, S)
                  componentHeightInU = unit.heightU || 1; // Nodes are 1U unless the vendor specifies otherwise (e.g. SMC 2U hybrid)
                  labelTitle = unit.label; // e.g., "S-Node 1"
                  labelDetails = `(${unit.ssdInfo})`;
                }
                
                const componentHeight = componentHeightInU * unitHeight;
                
                return (
                  <div 
                    key={`label-${rackIndex}-${index}`}
                    className="text-right pr-2 flex items-center justify-end"
                    style={{
                      position: 'absolute',
                      top: `${index * unitHeight}px`,
                      right: 0,
                      height: `${componentHeight}px`,
                      width: '100%'
                    }}
                  >
                    <span className="text-white text-xs font-bold">{labelTitle}</span>
                    <span className="text-gray-300 text-xs ml-1">{labelDetails}</span>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Rack itself */}
          <div
            className="relative"
            style={{ 
              height: `${rackHeight}px`, 
              width: `${rackWidth}px`,
              border: showRack ? '1px solid #444' : 'none',
              backgroundColor: showRack ? '#222' : 'transparent',
            }}
          >
            {/* Rack rails */}
            {showRack && (
              <>
                <div className="absolute left-0 top-0 w-3 h-full" style={{ backgroundColor: '#333' }}></div>
                <div className="absolute right-0 top-0 w-3 h-full" style={{ backgroundColor: '#333' }}></div>
              </>
            )}
            
            {/* Rack unit markers - only in detail view */}
            {isDetailView && [...Array(rackSize)].map((_, i) => (
              <div 
                key={`marker-${i}`}
                className="absolute right-4 text-gray-400 text-xs"
                style={{
                  top: `${i * unitHeight + unitHeight/2}px`,
                  transform: 'translateY(-50%)'
                }}
              >
                {rackSize - i}U
              </div>
            ))}
            
            {/* Components - centered in rack */}
            {rack.map((unit, index) => {
              if (unit.type === 'empty') return null;
              
              // For JBOD-only units (not combined with S-Node), render just the JBOD
              if (unit.isJbodOnly) {
                // Determine if this is a standard 4U JBOD or an 8U JBOD
                const isJbod8U = unit.type === 'jbod-8u';
                const jbodHeightInU = unit.heightU || (isJbod8U ? 8 : 4);
                const jbodHeight = jbodHeightInU * unitHeight;
                
                return (
                  <div 
                    key={`component-${rackIndex}-${index}`}
                    className="absolute left-1/2 transform -translate-x-1/2"
                    style={{
                      top: `${index * unitHeight}px`,
                      width: `${componentWidth}px`,
                      height: `${jbodHeight}px`,
                      overflow: 'hidden'
                    }}
                  >
                    <div className="relative w-full h-full">
                      <RackImage type={isJbod8U ? 'jbod-8u' : 'jbod'} vendor={hardwareVendor} />
                    </div>
                  </div>
                );
              }
              
              // Special handling for Dell 8U Hybrid Server
              if (unit.type === 'jbod-8u') {
                return (
                  <div 
                    key={`component-${rackIndex}-${index}`}
                    className="absolute left-1/2 transform -translate-x-1/2"
                    style={{
                      top: `${index * unitHeight}px`,
                      width: `${componentWidth}px`,
                      height: `${unitHeight * 8}px`, // 8U for Dell hybrid server
                      overflow: 'hidden'
                    }}
                  >
                    <div className="relative w-full h-full">
                      <RackImage type="jbod-8u" vendor={hardwareVendor} />
                    </div>
                  </div>
                );
              }
              
              // Standard components (D-Nodes, F-Nodes, S-Nodes)
              // Height honors the vendor's node height (e.g. SMC hybrid nodes are 2U)
              return (
                <div
                  key={`component-${rackIndex}-${index}`}
                  className="absolute left-1/2 transform -translate-x-1/2"
                  style={{
                    top: `${index * unitHeight}px`,
                    width: `${componentWidth}px`,
                    height: `${(unit.heightU || 1) * unitHeight}px`,
                    overflow: 'hidden'
                  }}
                >
                  <div className="relative w-full h-full">
                    <RackImage type={unit.type} vendor={hardwareVendor} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };
  
  // Function to render the modal content with a larger visualization
  const renderLargeVisualization = () => {
    // Limit the number of racks to display to prevent performance issues
    const maxDisplayedRacks = Math.min(5, Math.max(3, rackLayout.length));
    const displayedRacks = rackLayout.slice(0, maxDisplayedRacks);
    const hasMoreRacks = rackLayout.length > maxDisplayedRacks;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 p-6 rounded-lg max-w-full w-11/12 max-h-screen overflow-auto">
          <h2 className="text-xl font-bold mb-4 text-white">
            VDURA V5000 System Configuration - Rack Layout 
            {hasMoreRacks ? ` (Showing ${maxDisplayedRacks} of ${rackLayout.length} racks)` : ''}
          </h2>
          
          <div className="flex flex-row justify-center mb-6 overflow-x-auto" 
               style={{ gap: '40px' }}>
            {displayedRacks.map((rack, index) => (
              <div key={`rack-container-${index}`}>
                {renderRack(rack, index, true)}
              </div>
            ))}
          </div>
          
          {hasMoreRacks && (
            <div className="text-center mb-4 text-yellow-400">
              Note: Only showing first {maxDisplayedRacks} racks for performance reasons. 
              Total rack count: {rackLayout.length}
            </div>
          )}
          
          <div className="flex justify-between items-center bg-gray-700 p-4 rounded">
            <div className="legend flex flex-wrap gap-6">
              <div className="flex items-center">
                <div className="w-6 h-6 mr-2 overflow-hidden">
                  <RackImage type="dNode" vendor={hardwareVendor} />
                </div>
                <span className="text-sm text-white">D-Node (Director) - {productDb.ssdPerVelo} × {config.veloSsdCapacity}TB</span>
              </div>
              {config.afaEnabled && (
                <div className="flex items-center">
                  <div className="w-6 h-6 mr-2 overflow-hidden">
                    <RackImage type="fNode" vendor={hardwareVendor} />
                  </div>
                  <span className="text-sm text-white">F-Node (All-Flash Storage) - {productDb.vpodsPerNode} × {config.afaVpodSsdCapacity}TB</span>
                </div>
              )}
              {config.hybridEnabled && (
                <>
                  <div className="flex items-center">
                    <div className="w-6 h-6 mr-2 overflow-hidden">
                      <RackImage type="sNode" vendor={hardwareVendor} />
                    </div>
                    <span className="text-sm text-white">S-Node (Hybrid Storage) - {productDb.vpodsPerNode} × {config.hybridVpodSsdCapacity}TB</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-6 mr-2 overflow-hidden">
                      <RackImage type="jbod" vendor={hardwareVendor} />
                    </div>
                    <span className="text-sm text-white">JBOD: {config.jbodSize}-slot {config.hybridHddCapacity}TB HDDs</span>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Calculate system summary
  const totalRackUnits = metrics.totalRU || 0;
  const rackCount = Math.ceil(totalRackUnits / rackSize);
  
  return (
    <div className="rack-visualization">
      <div className="flex justify-between mb-2">
        <h3 className="text-lg font-semibold">System Visualization ({hardwareVendor})</h3>
        <div className="flex items-center">
          <label className="text-sm mr-2">Rack Size:</label>
          <select 
            value={selectedDropdownValue} 
            onChange={(e) => {
            const value = Number(e.target.value);
            setSelectedDropdownValue(value);
            if (value === 0) {
              setShowRack(false);
              setRackSize(42); // Use 42U for default calculations when "No Rack" is selected
            } else {
              setShowRack(true);
              setRackSize(value);
            }
            onRackSizeChange?.(value);
            }}
            className="bg-gray-700 text-white text-sm rounded px-1"
          >
            <option value={0}>No Rack</option>
            <option value={25}>25U</option>
            <option value={42}>42U</option>
            <option value={48}>48U</option>
            <option value={52}>52U</option>
          </select>
        </div>
      </div>
      
      <div className="flex items-start">
        <div className="flex flex-col">
          <div 
            className="flex flex-row overflow-x-auto cursor-pointer" 
            onClick={() => setIsModalOpen(true)}
            title="Click for larger view"
            style={{ gap: '20px' }}
          >
            {/* Limit the number of racks to display to prevent performance issues */}
            {rackLayout.slice(0, 3).map((rack, index) => (
              <div key={`rack-display-${index}`}>
                {renderRack(rack, index, false)}
              </div>
            ))}
            
            {/* Show indicator if there are more racks */}
            {rackLayout.length > 3 && (
              <div className="flex items-center justify-center px-4">
                <div className="bg-gray-800 border border-gray-600 rounded p-2 text-white text-sm">
                  +{rackLayout.length - 3} more racks
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-between mt-2">
            <div className="text-xs text-gray-400">
              Click on visualization for detailed view
            </div>
            <div className="text-xs text-gray-400">
              {rackCount} rack{rackCount > 1 ? 's' : ''}, {totalRackUnits.toFixed(0)}U total
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && renderLargeVisualization()}
    </div>
  );
};

export default RealisticRackVisualization;